import type { DockviewPanelApi } from 'dockview-react';
import { type ReactNode, useEffect, useState } from 'react';

import { HostVisibilityContext } from '../../surfaces/host-visibility';

/**
 * Project a dockview panel's visibility onto {@link HostVisibilityContext}
 * (架构方案 §5.7 视图缓存). Mounted by the panel *content* components, the only
 * place holding a `DockviewPanelApi`.
 *
 * Needed because a hidden dockview panel is not an unmounted one: dockview-react
 * keeps the React portal until the panel is *removed*, and a `renderer: 'always'`
 * panel keeps its DOM too — so without this the surfaces behind a document tab
 * would go on believing they are on screen.
 */
export function PanelVisibilityProvider({
  api,
  children,
}: {
  api: DockviewPanelApi;
  children: ReactNode;
}): ReactNode {
  const visible = usePanelVisible(api);
  return (
    <HostVisibilityContext.Provider value={visible}>
      {children}
    </HostVisibilityContext.Provider>
  );
}

/**
 * dockview's `isVisible` starts out `true` and only becomes accurate once the
 * first visibility event has fired, so cross-check the group's active panel for
 * the *initial* read — that is already set by then. Load-bearing for lazily
 * mounted content (§5.7 视图缓存): a restored inactive tab that claims to be on
 * screen for even one render is a tab that starts loading its iframe.
 */
function readPanelVisible(api: DockviewPanelApi): boolean {
  return api.isVisible && api.group.activePanel?.id === api.id;
}

function usePanelVisible(api: DockviewPanelApi): boolean {
  const [visible, setVisible] = useState(() => readPanelVisible(api));
  useEffect(() => {
    // Re-read on (re)subscribe: visibility can flip between render and effect.
    setVisible(api.isVisible);
    const subscription = api.onDidVisibilityChange((event) => {
      setVisible(event.isVisible);
    });
    return () => subscription.dispose();
  }, [api]);
  return visible;
}
